/**
 * TaskList Component
 *
 * A reusable checklist/task list component with drag-and-drop reordering,
 * user assignment, completion toggling, input capture, and delete functionality.
 *
 * @example
 * ```jsx
 * <TaskList
 *   items={checklistItems}
 *   team={team?.rows}
 *   onToggle={(taskId, completed, userInput) => handleToggle(taskId, completed, userInput)}
 *   onAssign={(taskId, userId) => handleAssign(taskId, userId)}
 *   onDelete={(taskId) => handleDelete(taskId)}
 *   onReorder={(items) => handleReorder(items)}
 *   onAdd={(content) => handleAdd(content)}
 *   emptyText="No tasks yet. Add one above!"
 * />
 * ```
 */

import { useState } from 'react';
import { Listbox, Menu } from '@headlessui/react';
import {
	CheckIcon,
	TrashIcon,
	Bars3Icon,
	PlusIcon,
	PencilSquareIcon,
	PaperClipIcon,
	ChevronRightIcon,
} from '@heroicons/react/20/solid';
import { AvatarGenerated } from '../Avatar';
import { getUserFullName } from '../helpers/functions';
import MarkdownOrHtmlDisplay from '../MarkdownOrHtmlDisplay';
import DatePicker from './DatePicker';
import { DueDateBadge } from './Badge';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

// Individual task item component for custom tasks (with checkbox)
const TaskItem = ({
	task,
	index,
	team,
	onToggle,
	onAssign,
	onDelete,
	onInputChange,
	onDueDateChange,
	onTaskClick,
	draggedTaskId,
	setDraggedTaskId,
	onDrop,
	readOnly = false, // Prevents drag, delete, assign
	disableToggle = false, // Prevents checkbox toggle (separate from readOnly)
}) => {
	const [inputValue, setInputValue] = useState(task.userInput || '');
	const [isExpanded, setIsExpanded] = useState(false);

	const displayText = task.title || task.content;
	const hasRichContent = displayText?.includes('<');
	const hasAttachments = task.attachments?.length > 0;
	const requiresInput = task.requiresInput || task.requires_input;
	const canComplete = !requiresInput || inputValue.trim();

	const handleToggle = () => {
		if (!canComplete && !task.completed) {
			// Expand to show input field if trying to complete without input
			setIsExpanded(true);
			return;
		}
		onToggle(task.id, task.completed, inputValue);
	};

	const handleInputBlur = () => {
		if (inputValue !== (task.userInput || '')) {
			onInputChange?.(task.id, inputValue);
		}
	};

	return (
		<li
			draggable={!readOnly}
			onDragStart={() => !readOnly && setDraggedTaskId(task.id)}
			onDragEnd={() => setDraggedTaskId(null)}
			onDragOver={(e) => e.preventDefault()}
			onDrop={() => onDrop(task.id, index)}
			className={classNames(
				'rounded-lg border bg-white group/task',
				draggedTaskId === task.id
					? 'border-indigo-300 bg-indigo-50 opacity-50'
					: 'border-gray-200 hover:border-gray-300',
				task.completed ? 'bg-gray-50' : '',
				onTaskClick && 'cursor-pointer',
			)}
			onClick={() => onTaskClick?.(task)}
		>
			<div className="flex items-center gap-3 p-1.5 w-full">
				{/* Drag handle */}
				{!readOnly && (
					<div
						className="cursor-grab text-gray-400 hover:text-gray-600 mt-0.5"
						onClick={(e) => e.stopPropagation()}
					>
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<circle cx="9" cy="5" r="1" fill="currentColor"></circle>
							<circle cx="9" cy="12" r="1" fill="currentColor"></circle>
							<circle cx="9" cy="19" r="1" fill="currentColor"></circle>
							<circle cx="15" cy="5" r="1" fill="currentColor"></circle>
							<circle cx="15" cy="12" r="1" fill="currentColor"></circle>
							<circle cx="15" cy="19" r="1" fill="currentColor"></circle>
						</svg>
					</div>
				)}

				{/* Checkbox */}
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						handleToggle();
					}}
					disabled={disableToggle}
					title={
						!canComplete && !task.completed
							? 'Input required before completing'
							: ''
					}
					className={classNames(
						'flex-shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors mt-0.5',
						task.completed
							? 'bg-indigo-600 border-indigo-600'
							: canComplete
								? 'border-gray-300 hover:border-indigo-400'
								: 'border-amber-400 bg-amber-50',
						disableToggle && 'cursor-default',
					)}
				>
					{task.completed && <CheckIcon className="h-3 w-3 text-white" />}
				</button>

				<div className="ml-2 flex-1 min-w-0 flex flex-col">
					{/* Right-aligned actions */}
					<div
						className="flex items-center gap-2 flex-shrink-0"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-center">
							{/* Assign dropdown */}
							{team && (
								<Listbox
									value={task.assigned_to || task.assignedTo || ''}
									onChange={(userId) => onAssign(task.id, userId || null)}
								>
									<div className="relative">
										<Listbox.Button className="flex items-center gap-1">
											<span
												className={classNames(
													'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-pointer',
													'bg-gray-100 text-gray-700 hover:bg-gray-200',
												)}
											>
												{task.assignedToUser ? (
													<span className="flex items-center gap-1.5">
														<AvatarGenerated
															id={getUserFullName(task.assignedToUser)}
															size="xxs"
														/>
														{getUserFullName(task.assignedToUser)}
													</span>
												) : (
													'Unassigned'
												)}
											</span>
										</Listbox.Button>
										<Listbox.Options className="absolute right-0 z-10 mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
											<Listbox.Option
												value=""
												className={({ active, selected }) =>
													classNames(
														'cursor-pointer select-none relative py-2 pl-3 pr-9',
														active ? 'bg-indigo-50' : '',
														selected ? 'bg-indigo-100' : '',
													)
												}
											>
												{({ selected }) => (
													<div className="flex items-center">
														<span
															className={classNames(
																'text-gray-500 text-xs',
																selected && 'font-semibold',
															)}
														>
															Unassigned
														</span>
														{selected && (
															<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
														)}
													</div>
												)}
											</Listbox.Option>
											{team
												?.filter((member) => member.active && !member.archived)
												?.map((member) => (
													<Listbox.Option
														key={member.id}
														value={member.id}
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center text-xs">
																<AvatarGenerated
																	id={getUserFullName(member)}
																	size="xxs"
																	className="mr-2"
																/>
																<span
																	className={selected ? 'font-semibold' : ''}
																>
																	{member?.first_name || member?.firstName}{' '}
																	{member?.last_name || member?.lastName}
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																)}
															</div>
														)}
													</Listbox.Option>
												))}
										</Listbox.Options>
									</div>
								</Listbox>
							)}

							{/* Due Date */}
							{onDueDateChange && (
								<div className="flex-shrink-0">
									<DatePicker
										value={task.dueDate || task.due_date || ''}
										onChange={(date) => onDueDateChange(task.id, date)}
										placeholder="Due date"
										size="sm"
										className="!min-w-[120px] border-none ring-0 md:shadow-none"
									/>
								</div>
							)}
						</div>
					</div>

					{/* Task content */}
					<div className="flex-1 min-w-0 mt-0.5 mb-1">
						{/* Title/Content - support rich text and markdown */}
						{hasRichContent ? (
							<div
								className={classNames(
									'text-xs truncate',
									task.completed
										? 'text-gray-400 line-through'
										: 'text-gray-900',
									onTaskClick && 'group-hover/task:text-indigo-600',
								)}
								title={displayText
									?.replace(/<[^>]*>/g, '')
									.replace(/[#*_~`]/g, '')}
							>
								<MarkdownOrHtmlDisplay content={displayText} />
							</div>
						) : (
							<div
								className={classNames(
									'text-xs truncate',
									task.completed
										? 'text-gray-400 line-through'
										: 'text-gray-900',
									onTaskClick && 'group-hover/task:text-indigo-600',
								)}
								title={displayText}
							>
								{displayText}
							</div>
						)}
					</div>
				</div>

				{/* Chevron indicator for clickable tasks */}
				{onTaskClick && (
					<ChevronRightIcon className="h-4 w-4 text-gray-300 group-hover/task:text-indigo-500 flex-shrink-0 transition-colors" />
				)}
			</div>

			{/* Indicators and expandable section - below main row */}
			{(requiresInput || hasAttachments) && (
				<div className="px-3 pb-3 pl-14">
					{/* Indicators row */}
					{(requiresInput || hasAttachments) && (
						<div className="flex items-center gap-2">
							{requiresInput && (
								<span
									className={classNames(
										'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded',
										task.userInput || inputValue
											? 'bg-green-100 text-green-700'
											: 'bg-amber-100 text-amber-700',
									)}
								>
									<PencilSquareIcon className="h-3 w-3" />
									{task.userInput || inputValue
										? 'Input provided'
										: 'Input required'}
								</span>
							)}
							{hasAttachments && (
								<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600">
									<PaperClipIcon className="h-3 w-3" />
									{task.attachments.length} attachment
									{task.attachments.length !== 1 ? 's' : ''}
								</span>
							)}
						</div>
					)}
					{/* Expandable section for input and attachments */}
					{(isExpanded || task.userInput) && (
						<div className="mt-2">
							{/* User input field */}
							{requiresInput && (
								<div className="mb-2">
									<label className="block text-[10px] font-medium text-gray-500 mb-1">
										{task.completed ? 'Response:' : 'Your response:'}
									</label>
									<textarea
										value={inputValue}
										onChange={(e) => setInputValue(e.target.value)}
										onBlur={handleInputBlur}
										placeholder="Enter your response..."
										rows={2}
										disabled={task.completed}
										className={classNames(
											'w-full rounded border px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500',
											task.completed
												? 'bg-gray-100 border-gray-200 text-gray-500'
												: 'border-gray-300',
										)}
									/>
								</div>
							)}

							{/* Attachments */}
							{hasAttachments && (
								<div>
									<label className="block text-[10px] font-medium text-gray-500 mb-1">
										Reference attachments:
									</label>
									<div className="flex flex-wrap gap-1">
										{task.attachments.map((att, idx) => (
											<a
												key={idx}
												href={att.url}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
											>
												<PaperClipIcon className="h-3 w-3" />
												{att.name || `Attachment ${idx + 1}`}
											</a>
										))}
									</div>
								</div>
							)}
						</div>
					)}
					{/* Expand/collapse button for items with input/attachments */}
					{!task.userInput && (
						<button
							type="button"
							onClick={() => setIsExpanded(!isExpanded)}
							className="text-[10px] text-indigo-600 hover:text-indigo-700 mt-1"
						>
							{isExpanded ? 'Hide details' : 'Show details'}
						</button>
					)}
				</div>
			)}
		</li>
	);
};

// SOP task item component (with "Mark as complete" button instead of checkbox)
const SopTaskItem = ({
	task,
	stepNumber,
	team,
	onToggle,
	onAssign,
	onInputChange,
	onDueDateChange,
}) => {
	const [inputValue, setInputValue] = useState(task.userInput || '');

	const hasRichContent = task.content?.includes('<');
	const hasAttachments = task.attachments?.length > 0;
	const requiresInput = task.requiresInput || task.requires_input;
	const canComplete = !requiresInput || inputValue.trim();

	const handleComplete = () => {
		if (canComplete) {
			onToggle(task.id, task.completed, inputValue);
		}
	};

	const handleMarkIncomplete = () => {
		onToggle(task.id, task.completed, inputValue);
	};

	const handleInputBlur = () => {
		if (inputValue !== (task.userInput || '')) {
			onInputChange?.(task.id, inputValue);
		}
	};

	// Format completion date
	const getCompletedDateString = () => {
		const completedAt = task.completedAt || task.completed_at;
		if (!completedAt) return '';
		const date = new Date(completedAt);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	// Get completed by user name
	const getCompletedByName = () => {
		const completedBy = task.completedByUser || task.completed_by_user;
		if (completedBy) {
			return getUserFullName(completedBy);
		}
		return 'Unknown';
	};

	return (
		<li
			className={classNames(
				'rounded-lg border bg-white',
				task.completed ? 'bg-gray-50' : '',
				'border-gray-200',
			)}
		>
			<div className="p-3">
				{/* Header row with content and assignment */}
				<div className="flex items-center gap-3">
					{/* Task content */}
					<div className="flex-1 min-w-0">
						{/* Step number */}
						<div
							className={classNames(
								'font-semibold text-xs mb-1',
								task.completed ? 'text-gray-400 line-through' : 'text-gray-700',
							)}
						>
							Step {stepNumber}
						</div>
						{/* Content - support rich text and markdown */}
						{hasRichContent ? (
							<div
								className={classNames(
									task.completed
										? 'text-gray-400 line-through'
										: 'text-gray-900',
								)}
							>
								<MarkdownOrHtmlDisplay content={task.content} />
							</div>
						) : (
							<div
								className={classNames(
									'text-xs',
									task.completed
										? 'text-gray-400 line-through'
										: 'text-gray-900',
								)}
							>
								{task.content}
							</div>
						)}

						{/* Attachments badges */}
						{hasAttachments && (
							<div className="flex items-center gap-2 mt-1">
								<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600">
									<PaperClipIcon className="h-3 w-3" />
									{task.attachments.length} attachment
									{task.attachments.length !== 1 ? 's' : ''}
								</span>
							</div>
						)}

						{/* Attachments list */}
						{hasAttachments && (
							<div className="mt-2">
								<label className="block text-[10px] font-medium text-gray-500 mb-1">
									Reference attachments:
								</label>
								<div className="flex flex-wrap gap-1">
									{task.attachments.map((att, idx) => (
										<a
											key={idx}
											href={att.url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-1 px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
										>
											<PaperClipIcon className="h-3 w-3" />
											{att.name || `Attachment ${idx + 1}`}
										</a>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Assign dropdown - only show when not completed */}
					{team && !task.completed && (
						<Listbox
							value={task.assigned_to || task.assignedTo || ''}
							onChange={(userId) => onAssign(task.id, userId || null)}
						>
							<div className="relative">
								<Listbox.Button className="flex items-center gap-1">
									<span
										className={classNames(
											'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-pointer',
											'bg-gray-100 text-gray-700 hover:bg-gray-200',
										)}
									>
										{task.assignedToUser ? (
											<span className="flex items-center gap-1.5">
												<AvatarGenerated
													id={getUserFullName(task.assignedToUser)}
													size="xxs"
												/>
												{getUserFullName(task.assignedToUser)}
											</span>
										) : (
											'Unassigned'
										)}
									</span>
								</Listbox.Button>
								<Listbox.Options className="absolute right-0 z-10 mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<Listbox.Option
										value=""
										className={({ active, selected }) =>
											classNames(
												'cursor-pointer select-none relative py-2 pl-3 pr-9',
												active ? 'bg-indigo-50' : '',
												selected ? 'bg-indigo-100' : '',
											)
										}
									>
										{({ selected }) => (
											<div className="flex items-center">
												<span
													className={classNames(
														'text-gray-500 text-xs',
														selected && 'font-semibold',
													)}
												>
													Unassigned
												</span>
												{selected && (
													<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
												)}
											</div>
										)}
									</Listbox.Option>
									{team
										?.filter((member) => member.active && !member.archived)
										?.map((member) => (
											<Listbox.Option
												key={member.id}
												value={member.id}
												className={({ active, selected }) =>
													classNames(
														'cursor-pointer select-none relative py-2 pl-3 pr-9',
														active ? 'bg-indigo-50' : '',
														selected ? 'bg-indigo-100' : '',
													)
												}
											>
												{({ selected }) => (
													<div className="flex items-center text-xs">
														<AvatarGenerated
															id={getUserFullName(member)}
															size="xxs"
															className="mr-2"
														/>
														<span className={selected ? 'font-semibold' : ''}>
															{member?.first_name || member?.firstName}{' '}
															{member?.last_name || member?.lastName}
														</span>
														{selected && (
															<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
														)}
													</div>
												)}
											</Listbox.Option>
										))}
								</Listbox.Options>
							</div>
						</Listbox>
					)}

					{/* Due Date - only show when not completed */}
					{onDueDateChange && !task.completed && (
						<div className="flex-shrink-0">
							<DatePicker
								value={task.dueDate || task.due_date || ''}
								onChange={(date) => onDueDateChange(task.id, date)}
								placeholder="Due date"
								size="sm"
								className="!min-w-[120px]"
							/>
						</div>
					)}
				</div>

				{/* Input and Mark as Complete section */}
				{!task.completed && (
					<div className="mt-3 flex items-end gap-2">
						{requiresInput ? (
							<>
								<input
									type="text"
									placeholder="Enter response..."
									value={inputValue}
									onChange={(e) => setInputValue(e.target.value)}
									onKeyDown={(e) => e.key === 'Enter' && handleInputBlur()}
									onBlur={handleInputBlur}
									className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
								/>
								<button
									type="button"
									onClick={handleComplete}
									disabled={!canComplete}
									className={classNames(
										'inline-flex items-center px-3 py-3 text-xs font-medium rounded-md transition-colors whitespace-nowrap',
										canComplete
											? 'bg-indigo-600 text-white hover:bg-indigo-700'
											: 'bg-gray-100 text-gray-400 cursor-not-allowed',
									)}
								>
									<PlusIcon className="h-4 w-4 mr-1" />
									Add
								</button>
							</>
						) : (
							<div className="flex justify-end w-full">
								<button
									type="button"
									onClick={handleComplete}
									className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
								>
									<CheckIcon className="h-4 w-4 mr-1" />
									Mark as Complete
								</button>
							</div>
						)}
					</div>
				)}

				{/* Completed state - show response and completed by info */}
				{task.completed && (
					<div className="mt-3">
						{/* Show response for completed tasks that had input */}
						{task.userInput && (
							<div className="mb-3">
								<label className="block text-[10px] font-medium text-gray-500 mb-1">
									Response:
								</label>
								<div className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2 border border-gray-200">
									{task.userInput}
								</div>
							</div>
						)}

						{/* Completed by button with dropdown */}
						<div className="flex justify-end">
							<Menu as="div" className="relative">
								<Menu.Button className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors">
									<CheckIcon className="h-4 w-4 mr-1" />
									Completed by {getCompletedByName()} on{' '}
									{getCompletedDateString()}
								</Menu.Button>
								<Menu.Items className="absolute right-0 z-10 mt-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<Menu.Item>
										{({ active }) => (
											<button
												type="button"
												onClick={handleMarkIncomplete}
												className={classNames(
													'block w-full text-left px-4 py-2 text-xs',
													active
														? 'bg-gray-100 text-gray-900'
														: 'text-gray-700',
												)}
											>
												Mark as incomplete
											</button>
										)}
									</Menu.Item>
								</Menu.Items>
							</Menu>
						</div>
					</div>
				)}
			</div>
		</li>
	);
};

// Add task input component
const AddTaskInput = ({ onAdd }) => {
	const [content, setContent] = useState('');

	const handleAdd = () => {
		if (content.trim()) {
			onAdd(content.trim());
			setContent('');
		}
	};

	return (
		<div className="flex items-center gap-2 mt-3">
			<input
				type="text"
				placeholder="Add a task..."
				value={content}
				onChange={(e) => setContent(e.target.value)}
				onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
				className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
			/>
			<button
				type="button"
				onClick={handleAdd}
				disabled={!content.trim()}
				className={classNames(
					'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
					content.trim()
						? 'bg-indigo-600 text-white hover:bg-indigo-700'
						: 'bg-gray-100 text-gray-400 cursor-not-allowed',
				)}
			>
				<PlusIcon className="h-4 w-4 mr-1" />
				Add
			</button>
		</div>
	);
};

// Main TaskList component
const TaskList = ({
	items = [],
	team,
	onToggle,
	onAssign,
	onDelete,
	onReorder,
	onAdd,
	onInputChange,
	onDueDateChange,
	onTaskClick,
	emptyText = 'No tasks yet. Add one above!',
	showAddInput = true,
	readOnly = false,
	className = '',
	sopName = null,
	sopDetails = null, // { name, version, versionId }
}) => {
	const [draggedTaskId, setDraggedTaskId] = useState(null);

	// Separate SOP items from custom items and sort completed to bottom
	const sortCompletedToBottom = (a, b) => {
		if (a.completed && !b.completed) return 1;
		if (!a.completed && b.completed) return -1;
		return 0;
	};

	const sopItems = items
		.filter((item) => item.sopId || item.sop_id)
		.sort(sortCompletedToBottom);
	const customItems = items
		.filter((item) => !item.sopId && !item.sop_id)
		.sort(sortCompletedToBottom);

	const handleDrop = (targetId) => {
		if (!draggedTaskId || draggedTaskId === targetId || !onReorder) return;

		// Only allow reordering within custom items
		const draggedItem = customItems.find((t) => t.id === draggedTaskId);
		if (!draggedItem) return;

		const draggedIndex = customItems.findIndex((t) => t.id === draggedTaskId);
		const newCustomItems = [...customItems];
		const [removed] = newCustomItems.splice(draggedIndex, 1);

		// Find the target index within custom items
		const targetCustomIndex = customItems.findIndex((t) => t.id === targetId);
		if (targetCustomIndex === -1) return;

		newCustomItems.splice(targetCustomIndex, 0, removed);

		// Reorder only custom items, keeping SOP items at their positions
		const reorderedItems = newCustomItems.map((item, idx) => ({
			id: item.id,
			sortOrder: sopItems.length + idx + 1,
		}));

		onReorder(reorderedItems);
	};

	const hasSopItems = sopItems.length > 0;
	const hasCustomItems = customItems.length > 0;
	const hasAnyItems = items.length > 0;

	return (
		<div className={classNames('space-y-4', className)}>
			{/* SOP Checklist Section */}
			{hasSopItems && (
				<div className="space-y-2">
					<div className="flex items-center gap-2 flex-wrap mb-3">
						<div
							className="text-xs text-gray-500 font-medium uppercase tracking-wider flex-1 min-w-0 truncate"
							title={sopDetails?.name || sopName || 'SOP Checklist'}
						>
							{sopDetails?.name || sopName || 'SOP Checklist'}
						</div>

						{sopDetails?.version && (
							<span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded font-medium">
								v{sopDetails.version}
							</span>
						)}
						<span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
							{sopItems.filter((t) => t.completed).length}/{sopItems.length}{' '}
							completed
						</span>
					</div>
					<ul className="space-y-2 border-l-2 border-indigo-200 pl-3 mt-3">
						{sopItems.map((task, index) => (
							<SopTaskItem
								key={task.id}
								task={task}
								stepNumber={index + 1}
								team={team}
								onToggle={onToggle}
								onAssign={onAssign}
								onInputChange={onInputChange}
								onDueDateChange={onDueDateChange}
							/>
						))}
					</ul>
				</div>
			)}

			{/* Custom Tasks Section */}
			{(hasCustomItems || (showAddInput && onAdd && !readOnly)) && (
				<div className="space-y-2 mt-3">
					{hasSopItems && (
						<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
							Additional Tasks
						</div>
					)}

					{/* Custom task list */}
					{hasCustomItems && (
						<ul className="space-y-2 mt-3">
							{customItems.map((task, index) => (
								<TaskItem
									key={task.id}
									task={task}
									index={index}
									team={team}
									onToggle={onToggle}
									onAssign={onAssign}
									onDelete={onDelete}
									onInputChange={onInputChange}
									onDueDateChange={onDueDateChange}
									onTaskClick={onTaskClick}
									draggedTaskId={draggedTaskId}
									setDraggedTaskId={setDraggedTaskId}
									onDrop={handleDrop}
									readOnly={readOnly}
								/>
							))}
						</ul>
					)}

					{/* Add task input - only for custom tasks */}
					{showAddInput && onAdd && !readOnly && <AddTaskInput onAdd={onAdd} />}
				</div>
			)}

			{/* Empty state - only show if no items at all */}
			{!hasAnyItems && !showAddInput && (
				<p className="text-sm text-gray-500 text-center py-8">{emptyText}</p>
			)}
		</div>
	);
};

export { TaskItem, AddTaskInput };
export default TaskList;
